import type { WiseguyAlert, WiseguyAlertFilters } from '../types/wiseguy';

/**
 * User-facing preset filters only.
 *
 * Server-side QF Steamroom routes already apply the product (WSJ) floor.
 * Client autopost_mode / meetsAutopostCriteria are retired — they encoded the
 * old Django rule and subtracted rows the server had approved.
 */
export function alertMatchesPresetFilters(
  alert: WiseguyAlert,
  f: WiseguyAlertFilters
): boolean {
  // Sweeps only (user toggle)
  if (f.sweeps_only && !alert.alert_type.toLowerCase().includes('sweep')) {
    return false;
  }

  // Opening only (user toggle)
  if (f.opening_only && !(alert.condition || '').toLowerCase().includes('opening')) {
    return false;
  }

  // Bias: array multiselect
  if (f.bias && f.bias.length > 0 && !f.bias.includes(alert.bias)) {
    return false;
  }

  // Contract type
  if (f.contract_type && alert.contract_type !== f.contract_type) {
    return false;
  }

  // Asset class: array multiselect
  if (f.asset_class && f.asset_class.length > 0) {
    const matches = f.asset_class.some(
      (ac) => ac.toLowerCase() === alert.asset_class.toLowerCase()
    );
    if (!matches) return false;
  }

  // Top bet
  if (f.top_bet === true && !alert.top_bet) return false;

  // Bet range
  if (f.bet_min && alert.bet < Number(f.bet_min)) return false;
  if (f.bet_max && alert.bet > Number(f.bet_max)) return false;

  // Multiplier range
  if (f.multiplier_min && parseFloat(alert.multiplier) < Number(f.multiplier_min)) {
    return false;
  }
  if (f.multiplier_max && parseFloat(alert.multiplier) > Number(f.multiplier_max)) {
    return false;
  }

  // DTE range (preset-level)
  if (f.dte_min !== undefined && f.dte_min !== '') {
    const dte = alert.time_to_expiry_days;
    if (dte === undefined || dte < Number(f.dte_min)) return false;
  }
  if (f.dte_max !== undefined && f.dte_max !== '') {
    const dte = alert.time_to_expiry_days;
    if (dte === undefined || dte > Number(f.dte_max)) return false;
  }

  // Expiry range (preset-level). The modal collects these as <input type="date">
  // and QF passes `expiry` through as ISO YYYY-MM-DD, so both sides are the same
  // format and compare correctly as strings. Deliberately NOT via `new Date()`:
  // `new Date('2026-09-15')` is parsed as UTC midnight, which is the prior day in
  // ET and would shift boundary matches by one.
  if (f.expiry_min && alert.expiry && alert.expiry < f.expiry_min) return false;
  if (f.expiry_max && alert.expiry && alert.expiry > f.expiry_max) return false;

  // Baskets is a portal-era field; QF rows do not carry it. Skip the check when
  // the alert has no baskets at all, rather than failing it — otherwise a saved
  // preset with baskets selected matches nothing and empties the whole tape.
  // Only alerts that actually have baskets are filtered against the selection.
  if (f.baskets && f.baskets.length > 0 && alert.baskets) {
    const alertBaskets = alert.baskets.toLowerCase();
    const matches = f.baskets.some((b) => alertBaskets.includes(b.toLowerCase()));
    if (!matches) return false;
  }

  return true;
}
